import React from 'react';
import { View, ScrollView, StyleSheet, Platform, useWindowDimensions, TouchableOpacity, Image, TextStyle, ViewStyle, ImageStyle } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Header } from './Header';
import { Hero } from './Hero';
import { Footer } from './Footer';
import { Typography } from '../ui';
import { useTheme } from '../../context/ThemeContext';
import { fonts } from '../../styles';
import { useI18n } from '../../i18n';
import { openApp } from './openApp';
import { landingLayout } from './constants';
import { fontWeights } from '../../styles/typographyTokens';

import { ACCENT_TEXT, accentTextOn } from '../../styles/accentText';
import { surfaceFill } from '../../styles/platformBackgrounds';
type LandingCard = {
  key: string;
  icon: keyof typeof Ionicons.glyphMap;
  path: string;
  color: string;
  bg: string;
};

type StepCard = {
  key: string;
  step: string;
  icon: keyof typeof Ionicons.glyphMap;
  path: string;
};

type ExperienceCard = {
  key: string;
  icon: keyof typeof Ionicons.glyphMap;
  path: string;
  image: any;
  color: string;
  bg: string;
};

const PLATFORM_CARDS: LandingCard[] = [
  { key: 'livePractices', icon: 'radio-outline', path: '/practice', color: '#E85D04', bg: '#FFF3EA' },
  { key: 'masters', icon: 'person-outline', path: '/masters', color: '#6B5CE7', bg: '#F0EDFF' },
  { key: 'quickRooms', icon: 'people-outline', path: '/dashboard', color: '#F04889', bg: '#FFF0F6' },
  { key: 'calendar', icon: 'calendar-number-outline', path: '/lives', color: '#F5A623', bg: '#FFF7DF' },
];

const STEPS: StepCard[] = [
  { key: 'createAccount', step: '01', icon: 'person-outline', path: '/register' },
  { key: 'findDirection', step: '02', icon: 'search-outline', path: '/masters' },
  { key: 'joinPractice', step: '03', icon: 'radio-outline', path: '/lives' },
  { key: 'practiceRegularly', step: '04', icon: 'heart-outline', path: '/practices' },
];

const EXPERIENCE: ExperienceCard[] = [
  { key: 'realtimePractice', icon: 'radio-outline', path: '/practices', image: require('../../../assets/generated/agape_bali_yoga_sunrise_v2.jpg'), color: '#E85D04', bg: '#FFF3EA' },
  { key: 'ownPace', icon: 'time-outline', path: '/lives', image: require('../../../assets/generated/agape_nature_meditation_lake_v2.jpg'), color: '#6B5CE7', bg: '#F0EDFF' },
  { key: 'peopleEvents', icon: 'people-outline', path: '/masters', image: require('../../../assets/images/circle-community.png'), color: '#2D8A4E', bg: '#EAF8EE' },
];

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <View style={styles.labelRow}>
    <View style={styles.labelDot} />
    <Typography style={styles.labelText}>{children}</Typography>
  </View>
);

export const LandingScreen: React.FC = () => {
  const { colors: themeColors, isDark } = useTheme();
  const { t } = useI18n('landing');
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  // CORE-1733 — разделы лендинга лежат на общем фоне страницы (на вебе
  // `pageBg` прозрачен), а не на сплошном белом `canvas`.
  const sectionBg = themeColors.pageBg;
  const cardBg = isDark ? themeColors.surface : '#FFFFFF';
  const elevatedBg = isDark ? themeColors.surfaceElevated : '#FFFFFF';
  const borderColor = isDark ? themeColors.border : '#F0E7DE';
  const subtleBorder = isDark ? themeColors.borderSubtle : '#F1EAE3';
  const mutedText = themeColors.textSecondary;
  const iconBg = (fallback: string, color: string) => isDark ? `${color}22` : fallback;
  // CORE-1733 (возврат QA 27.10) — карточки разделов на вебе — поверхности v4, а не
  // белые #FFFFFF. Натив — как был.
  const cardFill = (bg: string) => (Platform.OS === 'web' ? surfaceFill(isDark ? 'dark' : 'light') : { backgroundColor: bg, borderColor });

  return (
    <View style={[styles.screen, { backgroundColor: themeColors.pageBg }]}>
      <Header />
      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
        <Hero />

        <View style={[styles.section, styles.aboutSection, { backgroundColor: sectionBg, borderBottomColor: subtleBorder }]}>
          <View style={[styles.sectionInner, isMobile && styles.sectionInnerMobile]}>
            <View style={[styles.aboutGrid, isMobile && styles.stack]}>
              <View style={[styles.aboutTitleCol, isMobile && styles.aboutTitleColMobile]}>
                <View style={[styles.aboutIntroRow, isMobile && styles.aboutIntroRowMobile]}>
                  <View style={[styles.mandalaCircle, { borderColor: isDark ? 'rgba(255,112,32,0.32)' : '#F4C8A7', backgroundColor: isDark ? 'rgba(255,112,32,0.06)' : 'transparent' }]}>
                    <Ionicons name="leaf-outline" size={64} color={ACCENT_TEXT} />
                  </View>
                  <View style={styles.aboutIntroCopy}>
                    <SectionLabel>{t('landingV2.about.overline')}</SectionLabel>
                    <Typography style={[styles.aboutTitle, { color: themeColors.textPrimary }, isMobile && styles.aboutTitleMobile]}>
                      {t('landingV2.about.title')}
                    </Typography>
                  </View>
                </View>
              </View>

              <View style={styles.aboutTextCol}>
                {['p1', 'p2', 'p3', 'p4'].map((key) => (
                  <Typography key={key} style={[styles.aboutText, { color: mutedText }]}>
                    {t(`landingV2.about.${key}`)}
                  </Typography>
                ))}
              </View>
            </View>
          </View>
        </View>

        <View style={[styles.section, { backgroundColor: sectionBg }]}>
          <View style={[styles.sectionInner, isMobile && styles.sectionInnerMobile]}>
            <SectionLabel>{t('landingV2.platform.overline')}</SectionLabel>
            <Typography style={[styles.sectionTitle, { color: themeColors.textPrimary }, isMobile && styles.sectionTitleMobile]}>{t('landingV2.platform.title')}</Typography>
            <Typography style={[styles.sectionSub, { color: mutedText }]}>{t('landingV2.platform.subtitle')}</Typography>

            <View style={[styles.cardGrid, isMobile && styles.stack]}>
              {PLATFORM_CARDS.map((card) => (
                <TouchableOpacity
                  key={card.key}
                  activeOpacity={0.84}
                  onPress={() => openApp(card.path === '/practice' ? '/practices' : card.path)}
                  style={[styles.platformCard, cardFill(cardBg), isMobile ? styles.fullCard : styles.halfCard]}
                >
                  <View style={[styles.iconBadge, { backgroundColor: iconBg(card.bg, card.color) }]}>
                    <Ionicons name={card.icon} size={29} color={card.color} />
                  </View>
                  <Typography style={[styles.cardTitle, { color: themeColors.textPrimary }]}>{t(`landingV2.platform.cards.${card.key}.title`)}</Typography>
                  <Typography style={[styles.cardDesc, { color: mutedText }]}>{t(`landingV2.platform.cards.${card.key}.desc`)}</Typography>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>

        <View style={[styles.sectionCompact, { backgroundColor: sectionBg }]}>
          <View style={[styles.sectionInner, isMobile && styles.sectionInnerMobile]}>
            <SectionLabel>{t('landingV2.steps.overline')}</SectionLabel>
            <Typography style={[styles.sectionTitle, { color: themeColors.textPrimary }, isMobile && styles.sectionTitleMobile]}>{t('landingV2.steps.title')}</Typography>

            <View style={[styles.stepsGrid, isMobile && styles.stack]}>
              {STEPS.map((step, index) => (
                <View key={step.step} style={[styles.stepWrap, isMobile && styles.fullCard]}>
                  <TouchableOpacity activeOpacity={0.84} onPress={() => openApp(step.path)} style={[styles.stepCard, cardFill(cardBg)]}>
                    <Typography style={styles.stepNum}>{step.step}</Typography>
                    <View style={[styles.stepIconCircle, { borderColor: isDark ? 'rgba(255,112,32,0.32)' : '#F5D3BB' }]}>
                      <Ionicons name={step.icon} size={28} color={ACCENT_TEXT} />
                    </View>
                    <Typography style={[styles.stepTitle, { color: themeColors.textPrimary }]}>{t(`landingV2.steps.items.${step.key}.title`)}</Typography>
                    <Typography style={[styles.stepDesc, { color: mutedText }]}>{t(`landingV2.steps.items.${step.key}.desc`)}</Typography>
                  </TouchableOpacity>
                  {index < STEPS.length - 1 && !isMobile && (
                    <View style={[styles.stepArrow, { backgroundColor: themeColors.pageBase }]}>
                      <Ionicons name="arrow-forward" size={26} color={isDark ? 'rgba(255,112,32,0.38)' : '#E4B792'} />
                    </View>
                  )}
                </View>
              ))}
            </View>
          </View>
        </View>

        <View style={[styles.sectionCompact, { backgroundColor: sectionBg }]}>
          <View style={[styles.sectionInner, isMobile && styles.sectionInnerMobile]}>
            <SectionLabel>{t('landingV2.why.overline')}</SectionLabel>
            <Typography style={[styles.sectionTitle, { color: themeColors.textPrimary }, isMobile && styles.sectionTitleMobile]}>{t('landingV2.why.title')}</Typography>

            <View style={[styles.experienceGrid, isMobile && styles.stack]}>
              {EXPERIENCE.map((item) => (
                <TouchableOpacity key={item.key} activeOpacity={0.86} onPress={() => openApp(item.path)} style={[styles.experienceCard, cardFill(elevatedBg), isMobile && styles.fullCard]}>
                  <Image source={item.image} style={styles.experienceImage as ImageStyle} resizeMode="cover" />
                  <View style={styles.experienceContent}>
                    <View style={[styles.smallBadge, { backgroundColor: iconBg(item.bg, item.color) }]}>
                      <Ionicons name={item.icon} size={25} color={item.color} />
                    </View>
                    <Typography style={[styles.experienceTitle, { color: themeColors.textPrimary }]}>{t(`landingV2.why.cards.${item.key}.title`)}</Typography>
                    <Typography style={[styles.experienceDesc, { color: mutedText }]}>{t(`landingV2.why.cards.${item.key}.desc`)}</Typography>
                  </View>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>

        <Footer />
      </ScrollView>
    </View>
  );
};

const shadow = Platform.select({
  web: { boxShadow: '0 12px 32px rgba(66, 45, 28, 0.06)' },
  default: { elevation: 2 },
});

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  scrollContent: {
    paddingBottom: Platform.OS === 'web' ? 0 : 28,
  },
  section: {
    width: '100%',
    paddingVertical: 46,
    backgroundColor: '#FFFFFF',
  } as ViewStyle,
  sectionCompact: {
    width: '100%',
    paddingVertical: 34,
    backgroundColor: '#FFFFFF',
  } as ViewStyle,
  aboutSection: {
    borderBottomWidth: 1,
    borderBottomColor: '#F1EAE3',
  },
  sectionInner: {
    width: '100%',
    maxWidth: landingLayout.maxWidth,
    marginHorizontal: 'auto',
    paddingHorizontal: landingLayout.paddingH,
  } as ViewStyle,
  sectionInnerMobile: {
    paddingHorizontal: landingLayout.paddingH,
  } as ViewStyle,
  stack: {
    flexDirection: 'column',
  } as ViewStyle,
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  } as ViewStyle,
  labelDot: {
    width: 5,
    height: 5,
    borderRadius: 3,
    backgroundColor: '#E85D04',
  },
  labelText: {
    color: accentTextOn('#E85D04'),
    fontFamily: fonts.display,
    fontSize: 12,
    fontWeight: fontWeights.bold,
    letterSpacing: 2.4,
    textTransform: 'uppercase',
  } as TextStyle,
  aboutGrid: {
    flexDirection: 'row',
    gap: 84,
    alignItems: 'center',
  } as ViewStyle,
  aboutTitleCol: {
    flex: 1,
    minWidth: 360,
  } as ViewStyle,
  aboutTitleColMobile: {
    minWidth: 0,
    width: '100%',
  } as ViewStyle,
  aboutIntroRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 28,
  } as ViewStyle,
  aboutIntroRowMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 20,
  } as ViewStyle,
  aboutIntroCopy: {
    flex: 1,
    minWidth: 0,
  } as ViewStyle,
  aboutTextCol: {
    flex: 1,
    gap: 9,
  } as ViewStyle,
  mandalaCircle: {
    width: 112,
    height: 112,
    borderRadius: 56,
    borderWidth: 2,
    borderColor: '#F4C8A7',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  } as ViewStyle,
  aboutTitle: {
    color: '#1C1713',
    fontFamily: fonts.display,
    fontSize: 34,
    lineHeight: 40,
    fontWeight: fontWeights.bold,
    letterSpacing: -1,
    maxWidth: 520,
  } as TextStyle,
  aboutTitleMobile: {
    fontSize: 30,
    lineHeight: 36,
  } as TextStyle,
  aboutText: {
    color: '#4E4741',
    fontSize: 15,
    lineHeight: 24,
    fontWeight: '500',
  } as TextStyle,
  sectionTitle: {
    color: '#1C1713',
    fontFamily: fonts.display,
    fontSize: 35,
    lineHeight: 42,
    fontWeight: fontWeights.bold,
    letterSpacing: -1.1,
  } as TextStyle,
  sectionTitleMobile: {
    fontSize: 30,
    lineHeight: 36,
  } as TextStyle,
  sectionSub: {
    color: '#6B625B',
    fontSize: 16,
    lineHeight: 24,
    marginTop: 6,
    marginBottom: 24,
  } as TextStyle,
  cardGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 18,
  } as ViewStyle,
  fullCard: {
    width: '100%',
  } as ViewStyle,
  halfCard: {
    width: '48%',
  } as ViewStyle,
  platformCard: {
    minHeight: 174,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#F0E7DE',
    borderRadius: 16,
    padding: 24,
    ...shadow,
  } as ViewStyle,
  iconBadge: {
    width: 54,
    height: 54,
    borderRadius: 27,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 18,
  } as ViewStyle,
  smallBadge: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  } as ViewStyle,
  cardTitle: {
    color: '#1C1713',
    fontFamily: fonts.display,
    fontSize: 18,
    lineHeight: 24,
    fontWeight: fontWeights.bold,
    marginBottom: 10,
  } as TextStyle,
  cardDesc: {
    color: '#5C5652',
    fontSize: 14,
    lineHeight: 22,
    fontWeight: '500',
    marginBottom: 14,
  } as TextStyle,
  stepsGrid: {
    flexDirection: 'row',
    gap: 14,
    marginTop: 20,
  } as ViewStyle,
  stepWrap: {
    flex: 1,
    position: 'relative',
  } as ViewStyle,
  stepCard: {
    minHeight: 174,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#F0E7DE',
    borderRadius: 16,
    padding: 22,
    ...shadow,
  } as ViewStyle,
  stepNum: {
    color: accentTextOn('#F05A00'),
    fontFamily: fonts.display,
    fontSize: 22,
    lineHeight: 24,
    fontWeight: fontWeights.bold,
    marginBottom: 12,
    // CORE-1733 (возврат QA 28.10) — на вебе номер — обычный текст 16 px (размер
    // задаёт роль Typography), и .82 на поверхности v4 давало 4.07:1. Акцентный
    // текст непрозрачным держит 4.5+; натив — как был.
    opacity: Platform.OS === 'web' ? 1 : 0.82,
  } as TextStyle,
  stepIconCircle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: '#F5D3BB',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  } as ViewStyle,
  stepTitle: {
    color: '#1C1713',
    fontFamily: fonts.display,
    fontSize: 16,
    lineHeight: 21,
    fontWeight: fontWeights.bold,
    marginBottom: 9,
  } as TextStyle,
  stepDesc: {
    color: '#5C5652',
    fontSize: 13,
    lineHeight: 20,
    fontWeight: '500',
    marginBottom: 12,
  } as TextStyle,
  stepArrow: {
    position: 'absolute',
    right: -24,
    top: 84,
    zIndex: 4,
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  } as ViewStyle,
  experienceGrid: {
    flexDirection: 'row',
    gap: 18,
    marginTop: 22,
  } as ViewStyle,
  experienceCard: {
    flex: 1,
    minHeight: 220,
    borderWidth: 1,
    borderColor: '#F0E7DE',
    borderRadius: 18,
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    flexDirection: 'row',
    ...shadow,
  } as ViewStyle,
  experienceImage: {
    width: 130,
    height: '100%',
  } as ImageStyle,
  experienceContent: {
    flex: 1,
    padding: 20,
  } as ViewStyle,
  experienceTitle: {
    color: '#1C1713',
    fontFamily: fonts.display,
    fontSize: 17,
    lineHeight: 23,
    fontWeight: fontWeights.bold,
    marginBottom: 9,
  } as TextStyle,
  experienceDesc: {
    color: '#5C5652',
    fontSize: 13,
    lineHeight: 20,
    fontWeight: '500',
    marginBottom: 12,
  } as TextStyle,
});
